import React, { useEffect, useState } from 'react';
import ApolloError from '../../../shared/components/ApolloError';
import GET_PERCENTAGE_VISIBILITY_SETTINGS from '../../queries/getPercentageVisibilitySettings';
import { useMutation, useQuery } from '@apollo/client';
import { BlockLabel, TitleLabel } from '../common/Labels';
import ActionButtonGroup from '../../../shared/components/ActionButtonsGroup';
import VisibilitySettings from './VisibilitySettings';
import SET_PERCENTAGE_VISIBILITY_SETTINGS from '../../queries/setPercentageVisibilitySettings';
import visibilityValueFieldToTextMap from './visibilityValueFieldToTextMap';
import { getHasHavePlural } from '../../../../utils/plurals';
import { joinFormatted } from '../../../../utils/arrayExtensions';
import SuccessMessage from '../../../shared/components/SuccessMessage';

/**
 * @typedef {object} PercentageVisibilitySettings
 * @property {boolean} VisibleOnAggregateAppraisalReport
 * @property {boolean} VisibleOnDetailedAppraisalReport
 * @property {boolean} VisibleOnAppraisalView
 * @property {boolean} VisibleOnAppraisalComparison
 */

const settingsQueryResponseToStateMap = response => {
  return {
    VisibleOnAggregateAppraisalReport: response.VisibleOnAggregateAppraisalReport,
    VisibleOnDetailedAppraisalReport: response.VisibleOnDetailedAppraisalReport,
    VisibleOnAppraisalView: response.VisibleOnAppraisalView,
    VisibleOnAppraisalComparison: response.VisibleOnAppraisalComparison
  };
};

const detectChanges = (obj1, obj2) => {
  const keys1 = Object.keys(obj1).filter(key => key !== '__typename');

  return keys1.filter(key => obj1[key] !== obj2[key]);
};

/**
 * @typedef {object} OrganizationReportManagementProps
 * @property {number} [orgUnitId]
 */

/**
 * @param {OrganizationReportManagementProps} props
 */
const OrganizationReportManagement = props => {
  const [selectedVisibilitySettings, setCurrentSelectedSettings] = useState(
    /** @type {PercentageVisibilitySettings | undefined} */ (undefined)
  );
  const [isEdit, setIsEdit] = useState(false);
  const [changes, setChanges] = useState([]);
  const [successMessageText, setSuccessMessageText] = useState('');

  const {
    data: visibilitySettings,
    error,
    loading
  } = useQuery(GET_PERCENTAGE_VISIBILITY_SETTINGS, {
    variables: { orgUnitId: props.orgUnitId }
  });

  const [setData, { loading: savingSettings, error: savingOrgSettingsError }] = useMutation(
    SET_PERCENTAGE_VISIBILITY_SETTINGS,
    {
      refetchQueries: [
        {
          query: GET_PERCENTAGE_VISIBILITY_SETTINGS,
          variables: { orgUnitId: props.orgUnitId }
        }
      ]
    }
  );

  useEffect(() => {
    if (!visibilitySettings) {
      return;
    }

    const state = settingsQueryResponseToStateMap(
      visibilitySettings.organizationVisibilitySettings
    );

    setChanges([]);
    setCurrentSelectedSettings(state);
  }, [visibilitySettings]);

  const handleCancelClick = () => {
    const state = settingsQueryResponseToStateMap(
      visibilitySettings.organizationVisibilitySettings
    );

    setCurrentSelectedSettings(state);
    setIsEdit(false);
  };

  const handleEditClick = () => {
    setSuccessMessageText('');
    setIsEdit(true);
  };

  const handleSubmitClick = async () => {
    setIsEdit(false);
    await setData({
      variables: { orgUnitId: props.orgUnitId, data: selectedVisibilitySettings }
    });

    const changedSettingNames = changes.map(c => visibilityValueFieldToTextMap.get(c)).sort();
    const changedSettingsText = joinFormatted(changedSettingNames);

    const message = `Organization Page - ${changedSettingsText} ${getHasHavePlural(
      changes.length
    )} been updated.`;
    setSuccessMessageText(message);
  };

  const handleSettingChange = (checked, field) => {
    const copy = {
      ...selectedVisibilitySettings
    };
    copy[field] = checked;

    const changes = detectChanges(visibilitySettings.organizationVisibilitySettings, copy);
    setChanges(changes);

    setCurrentSelectedSettings(copy);
  };

  if (error || savingOrgSettingsError) {
    return <ApolloError />;
  }

  return (
    <div className='organization-aggregate-percentage'>
      <TitleLabel className='title-label'>Organization Report Management</TitleLabel>
      <div className='buttons-container'>
        <p>
          This page allows Organizations to manage data visibility. Show or hide the following data
          on any affiliated reports.
        </p>
        <ActionButtonGroup
          isEdit={isEdit}
          isSubmitting={savingSettings}
          isSubmitDisabled={!changes.length}
          disabled={loading}
          handleEditClick={handleEditClick}
          handleCancelClick={handleCancelClick}
          handleSubmitClick={handleSubmitClick}
        />
      </div>
      <BlockLabel className='block-label'>Aggregate Percentage</BlockLabel>
      <p>This displays the Aggregate Percentage on the given pages to members</p>
      <div className='settings-root'>
        <VisibilitySettings
          isEdit={isEdit}
          handleSettingChange={handleSettingChange}
          settings={selectedVisibilitySettings}
          loading={loading}
        />
        <div className='success-message'>
          <SuccessMessage message={successMessageText} />
        </div>
      </div>
    </div>
  );
};

export default OrganizationReportManagement;
